<template>
  <button
      ref="buttonRef"
      class="ai-chat-menu-button"
      type="button"
      aria-label="Open Metersuite AI chat"
      title="Metersuite AI"
      @pointerenter="hoverTarget = 1"
      @pointerleave="handlePointerLeave"
      @pointermove="handlePointerMove"
      @click.stop="handleClick"
  >
    <span class="ai-chat-orb" aria-hidden="true">
      <canvas ref="canvasRef" class="ai-chat-canvas"></canvas>
    </span>
    <span class="ai-chat-label">Metersuite AI</span>
  </button>
</template>

<script setup>
/* global defineEmits */
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import * as THREE from 'three';

const emit = defineEmits(['open']);

const buttonRef = ref(null);
const canvasRef = ref(null);

let renderer = null;
let material = null;
let geometry = null;
let animationFrame = 0;
let resizeObserver = null;
let uniforms = null;
let hoverTarget = 0;
let hoverValue = 0;
let clickBurst = 0;

const pointerTarget = new THREE.Vector2(0, 0);
const pointerValue = new THREE.Vector2(0, 0);

const vertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  uniform float uTime;
  uniform vec2 uResolution;
  uniform vec2 uPointer;
  uniform float uHover;
  uniform float uClick;
  uniform float uReducedMotion;

  varying vec2 vUv;

  float saturate(float value) {
    return clamp(value, 0.0, 1.0);
  }

  vec3 rotateX(vec3 p, float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return vec3(p.x, c * p.y - s * p.z, s * p.y + c * p.z);
  }

  vec3 rotateY(vec3 p, float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
  }

  vec3 rotateZ(vec3 p, float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z);
  }

  float hash31(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
  }

  float noise3(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);

    f = f * f * (3.0 - 2.0 * f);

    float n000 = hash31(i + vec3(0.0, 0.0, 0.0));
    float n100 = hash31(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash31(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash31(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash31(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash31(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash31(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash31(i + vec3(1.0, 1.0, 1.0));

    float nx00 = mix(n000, n100, f.x);
    float nx10 = mix(n010, n110, f.x);
    float nx01 = mix(n001, n101, f.x);
    float nx11 = mix(n011, n111, f.x);

    float nxy0 = mix(nx00, nx10, f.y);
    float nxy1 = mix(nx01, nx11, f.y);

    return mix(nxy0, nxy1, f.z);
  }

  float sdEllipsoid(vec3 p, vec3 r) {
    float k0 = length(p / r);
    float k1 = length(p / (r * r));
    return k0 * (k0 - 1.0) / k1;
  }

  float smoothUnion(float a, float b, float k) {
    float h = saturate(0.5 + 0.5 * (b - a) / k);
    return mix(b, a, h) - k * h * (1.0 - h);
  }

  float smoothMax(float a, float b, float k) {
    float h = saturate(0.5 - 0.5 * (b - a) / k);
    return mix(b, a, h) + k * h * (1.0 - h);
  }

  float smoothSubtract(float baseShape, float cutter, float k) {
    return smoothMax(baseShape, -cutter, k);
  }

  float lobe(vec3 p, vec3 offset, vec3 radius, vec3 rotation) {
    vec3 q = p - offset;

    q = rotateX(q, rotation.x);
    q = rotateY(q, rotation.y);
    q = rotateZ(q, rotation.z);

    return sdEllipsoid(q, radius);
  }

  float livingBlob(vec3 p) {
    float motion = 1.0 - uReducedMotion;
    float t = uTime * mix(1.0, 0.12, uReducedMotion);

    vec3 q = p;

    q.x += uPointer.x * 0.10 * uHover;
    q.y += uPointer.y * 0.08 * uHover;

    q = rotateY(q, sin(t * 0.36) * 0.24 + uPointer.x * 0.18 * uHover);
    q = rotateX(q, cos(t * 0.31) * 0.14 - uPointer.y * 0.13 * uHover);
    q = rotateZ(q, sin(t * 0.24) * 0.12);

    float breathe = 1.0 + sin(t * 1.35) * 0.020 * motion + uClick * 0.040;

    vec3 corePoint = q;

    corePoint.x += sin(q.y * 3.8 + q.z * 1.7 + t * 0.95) * 0.060 * motion;
    corePoint.y += sin(q.z * 3.4 - q.x * 1.9 - t * 0.82) * 0.052 * motion;
    corePoint.z += sin(q.x * 3.6 + q.y * 1.8 + t * 0.78) * 0.044 * motion;

    float broadWobble =
      sin(q.x * 5.2 + q.y * 2.1 + t * 0.85) * 0.020 +
      sin(q.y * 5.7 - q.z * 2.4 - t * 0.72) * 0.018 +
      sin(q.z * 5.0 + q.x * 2.6 + t * 0.66) * 0.016;

    float liquidNoise =
      noise3(q * 3.1 + vec3(t * 0.16, -t * 0.11, t * 0.09)) * 0.050 +
      noise3(q * 6.4 + vec3(-t * 0.09, t * 0.13, -t * 0.07)) * 0.024;

    float d = sdEllipsoid(corePoint, vec3(0.66, 0.64, 0.55) * breathe);

    d -= (liquidNoise + broadWobble) * motion;

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(-0.43 + sin(t * 1.10) * 0.024 * motion, 0.18, 0.02),
        vec3(0.31, 0.45, 0.31) * breathe,
        vec3(0.20, -0.34, -0.98 + sin(t * 0.70) * 0.12 * motion)
      ),
      0.34
    );

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(0.23, 0.35 + cos(t * 0.92) * 0.024 * motion, -0.01),
        vec3(0.37, 0.39, 0.33),
        vec3(-0.24, 0.34, 0.34 + sin(t * 0.86) * 0.11 * motion)
      ),
      0.33
    );

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(-0.31, -0.37, 0.02),
        vec3(0.51, 0.31, 0.30),
        vec3(0.22, 0.14, 0.28 + cos(t * 0.75) * 0.10 * motion)
      ),
      0.36
    );

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(0.45 + sin(t * 0.84) * 0.020 * motion, -0.25, -0.01),
        vec3(0.31, 0.45, 0.30),
        vec3(-0.18, -0.38, -0.74 + sin(t * 0.62) * 0.12 * motion)
      ),
      0.33
    );

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(-0.50, 0.40, -0.02),
        vec3(0.30, 0.23, 0.25),
        vec3(0.20, -0.18, -0.38)
      ),
      0.27
    );

    d = smoothUnion(
      d,
      lobe(
        q,
        vec3(0.49, 0.22, -0.02),
        vec3(0.25, 0.30, 0.25),
        vec3(-0.16, 0.26, 0.48)
      ),
      0.26
    );

    float centerFold = lobe(
      q,
      vec3(-0.03 + sin(t * 0.65) * 0.025 * motion, 0.02, 0.34),
      vec3(0.23, 0.40, 0.16),
      vec3(0.20, -0.22, 0.64 + sin(t * 0.8) * 0.10 * motion)
    );

    float lowerFold = lobe(
      q,
      vec3(0.13, -0.24 + cos(t * 0.72) * 0.020 * motion, 0.35),
      vec3(0.19, 0.31, 0.14),
      vec3(-0.18, 0.20, -0.52)
    );

    float sideFold = lobe(
      q,
      vec3(0.37, 0.04, 0.32),
      vec3(0.17, 0.33, 0.13),
      vec3(0.12, -0.18, 0.22 + sin(t * 0.7) * 0.08 * motion)
    );

    float topFold = lobe(
      q,
      vec3(-0.12, 0.38, 0.30),
      vec3(0.30, 0.14, 0.12),
      vec3(0.10, 0.24, -0.30 + sin(t * 0.62) * 0.08 * motion)
    );

    d = smoothSubtract(d, centerFold, 0.22);
    d = smoothSubtract(d, lowerFold, 0.21);
    d = smoothSubtract(d, sideFold, 0.20);
    d = smoothSubtract(d, topFold, 0.19);

    float fineRipple = noise3(q * 8.0 + vec3(t * 0.18, -t * 0.14, t * 0.10)) - 0.5;

    d += fineRipple * 0.026 * motion;

    return d;
  }

  vec3 calcNormal(vec3 p) {
    vec2 e = vec2(0.0022, 0.0);

    return normalize(vec3(
      livingBlob(p + e.xyy) - livingBlob(p - e.xyy),
      livingBlob(p + e.yxy) - livingBlob(p - e.yxy),
      livingBlob(p + e.yyx) - livingBlob(p - e.yyx)
    ));
  }

  float softShadow(vec3 ro, vec3 rd) {
    float result = 1.0;
    float travel = 0.035;

    for (int i = 0; i < 32; i++) {
      vec3 p = ro + rd * travel;
      float h = livingBlob(p);

      result = min(result, 12.0 * h / travel);
      travel += clamp(h, 0.018, 0.09);

      if (result < 0.02 || travel > 2.4) {
        break;
      }
    }

    return saturate(result);
  }

  void main() {
    vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / uResolution.y;

    vec3 ro = vec3(0.0, 0.0, 3.65);
    vec3 rd = normalize(vec3(uv * 1.35, -2.45));

    float travel = 0.0;
    bool hit = false;

    for (int i = 0; i < 120; i++) {
      vec3 p = ro + rd * travel;
      float d = livingBlob(p);

      if (d < 0.0018) {
        hit = true;
        break;
      }

      travel += clamp(d * 0.68, 0.003, 0.08);

      if (travel > 6.2) {
        break;
      }
    }

    if (!hit) {
      discard;
    }

    vec3 p = ro + rd * travel;
    vec3 n = calcNormal(p);
    vec3 v = normalize(-rd);

    vec3 keyLight = normalize(vec3(-0.48, 0.82, 0.56));
    vec3 fillLight = normalize(vec3(0.28, 0.18, 0.92));
    vec3 redLight = normalize(vec3(0.76, -0.18, 0.28));

    float shadow = softShadow(p + n * 0.015, keyLight);

    float diffuse = max(dot(n, keyLight), 0.0) * shadow;
    float fill = max(dot(n, fillLight), 0.0);
    float redSide = pow(max(dot(n, redLight), 0.0), 4.0);

    float fresnel = pow(1.0 - saturate(dot(n, v)), 2.25);
    float specA = pow(max(dot(reflect(-keyLight, n), v), 0.0), 96.0);
    float specB = pow(max(dot(reflect(-fillLight, n), v), 0.0), 42.0);

    vec3 deepGlass = vec3(0.018, 0.026, 0.060);
    vec3 iceGlass = vec3(0.62, 0.76, 0.95);
    vec3 rimBlue = vec3(0.45, 0.64, 1.00);

    vec3 color = mix(deepGlass, iceGlass, 0.18 + diffuse * 0.68);
    color += rimBlue * fresnel * 0.86;
    color += vec3(1.0) * specA * 1.82;
    color += vec3(0.70, 0.86, 1.0) * specB * 0.52;
    color += vec3(0.11, 0.18, 0.38) * fill * 0.42;

    float innerLineA = smoothstep(
      0.105,
      0.0,
      abs(p.x * 0.95 + sin(p.y * 7.0 + uTime * 1.7) * 0.075)
    );

    innerLineA *= smoothstep(0.78, 0.08, length(p.xy - vec2(0.06, -0.06)));
    innerLineA *= smoothstep(0.56, -0.28, p.z);

    float innerLineB = smoothstep(
      0.075,
      0.0,
      abs(p.y * 0.78 - p.x * 0.42 + sin(p.x * 8.5 - uTime * 1.2) * 0.05)
    );

    innerLineB *= smoothstep(0.62, 0.06, length(p.xy - vec2(0.25, -0.22)));
    innerLineB *= smoothstep(0.48, -0.24, p.z);

    float redPulse = 0.55 + 0.45 * sin(uTime * 2.35);
    float redCore = (innerLineA * 0.65 + innerLineB * 0.55) * redPulse;
    redCore *= 0.35 + uHover * 0.38 + uClick * 0.85;

    color += vec3(1.00, 0.025, 0.052) * redCore;
    color += vec3(1.00, 0.04, 0.06) * redSide * (0.11 + uHover * 0.13);

    float grain = noise3(p * 9.0 + vec3(uTime * 0.08));
    color += (grain - 0.5) * vec3(0.034, 0.044, 0.062);

    float alpha = 0.65 + fresnel * 0.28 + specA * 0.10;
    alpha += uHover * 0.05;
    alpha = saturate(alpha);

    gl_FragColor = vec4(color, alpha);
  }
`;

const resizeRenderer = () => {
  if (!renderer || !canvasRef.value || !uniforms) return;

  const rect = canvasRef.value.getBoundingClientRect();
  const width = Math.max(1, Math.floor(rect.width));
  const height = Math.max(1, Math.floor(rect.height));

  renderer.setSize(width, height, false);

  const pixelRatio = renderer.getPixelRatio();
  uniforms.uResolution.value.set(width * pixelRatio, height * pixelRatio);
};

const handlePointerMove = (event) => {
  const rect = buttonRef.value?.getBoundingClientRect();

  if (!rect?.width || !rect.height) return;

  const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -(((event.clientY - rect.top) / rect.height) * 2 - 1);

  pointerTarget.set(
      THREE.MathUtils.clamp(x, -1, 1),
      THREE.MathUtils.clamp(y, -1, 1)
  );
};

const handlePointerLeave = () => {
  hoverTarget = 0;
  pointerTarget.set(0, 0);
};

const animate = () => {
  if (!renderer || !material || !uniforms) return;

  const time = performance.now() * 0.001;

  hoverValue += (hoverTarget - hoverValue) * 0.085;
  pointerValue.lerp(pointerTarget, 0.08);
  clickBurst *= 0.9;

  uniforms.uTime.value = time;
  uniforms.uHover.value = hoverValue;
  uniforms.uPointer.value.copy(pointerValue);
  uniforms.uClick.value = clickBurst;

  renderer.render(material.userData.scene, material.userData.camera);
  animationFrame = requestAnimationFrame(animate);
};

const initRenderer = () => {
  if (!canvasRef.value || renderer) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 0;

  renderer = new THREE.WebGLRenderer({
    canvas: canvasRef.value,
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
    powerPreference: 'high-performance'
  });

  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  uniforms = {
    uTime: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPointer: { value: new THREE.Vector2(0, 0) },
    uHover: { value: 0 },
    uClick: { value: 0 },
    uReducedMotion: { value: prefersReducedMotion }
  };

  material = new THREE.ShaderMaterial({
    transparent: true,
    depthTest: false,
    depthWrite: false,
    uniforms,
    vertexShader,
    fragmentShader
  });

  material.userData.scene = scene;
  material.userData.camera = camera;

  geometry = new THREE.PlaneGeometry(2, 2);
  scene.add(new THREE.Mesh(geometry, material));

  resizeRenderer();

  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(resizeRenderer);
    resizeObserver.observe(canvasRef.value);
  }

  window.addEventListener('resize', resizeRenderer);
  animate();
};

const cleanupRenderer = () => {
  cancelAnimationFrame(animationFrame);
  window.removeEventListener('resize', resizeRenderer);
  resizeObserver?.disconnect();
  resizeObserver = null;

  geometry?.dispose();
  material?.dispose();
  renderer?.dispose();

  geometry = null;
  material = null;
  renderer = null;
  uniforms = null;
};

const handleClick = () => {
  clickBurst = 1;
  emit('open');
};

onMounted(() => {
  nextTick(initRenderer);
});

onBeforeUnmount(cleanupRenderer);
</script>

<style scoped>
.ai-chat-menu-button {
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  min-height: 60px;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.94);
  transform: translateZ(0);
  transition: transform 180ms ease, filter 180ms ease;
}

.ai-chat-menu-button:hover,
.ai-chat-menu-button:focus-visible {
  filter: drop-shadow(0 12px 28px rgba(91, 182, 255, 0.16));
  outline: none;
  transform: translateY(-1px);
}

.ai-chat-menu-button:active {
  transform: translateY(0) scale(0.98);
}

.ai-chat-orb {
  position: relative;
  width: 60px;
  height: 60px;
  flex: 0 0 60px;
  overflow: hidden;
  border-radius: 50%;
  isolation: isolate;
  background:
    radial-gradient(circle at 32% 24%, rgba(230, 244, 255, 0.14), transparent 28%),
    radial-gradient(circle at 72% 70%, rgba(255, 20, 48, 0.18), transparent 28%),
    radial-gradient(circle at 50% 50%, rgba(10, 13, 22, 0.9), rgba(2, 3, 6, 0.98) 72%);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.46),
    0 0 0 1px rgba(205, 230, 255, 0.08) inset,
    0 0 32px rgba(105, 150, 255, 0.12);
}

.ai-chat-orb::before {
  content: "";
  position: absolute;
  inset: -35%;
  z-index: 0;
  background:
    conic-gradient(
      from 180deg,
      transparent,
      rgba(120, 170, 255, 0.28),
      transparent,
      rgba(255, 24, 52, 0.24),
      transparent
    );
  opacity: 0.72;
  filter: blur(16px);
  animation: aiAuraSpin 8s linear infinite;
}

.ai-chat-orb::after {
  content: "";
  position: absolute;
  inset: 8%;
  z-index: 2;
  border-radius: inherit;
  pointer-events: none;
  background:
    radial-gradient(circle at 34% 22%, rgba(255, 255, 255, 0.13), transparent 22%),
    radial-gradient(circle at 56% 14%, rgba(210, 235, 255, 0.08), transparent 26%);
  mix-blend-mode: screen;
}

.ai-chat-canvas {
  position: absolute;
  inset: -18%;
  z-index: 1;
  width: 136%;
  height: 136%;
  display: block;
  filter:
    drop-shadow(0 8px 16px rgba(80, 122, 210, 0.24))
    drop-shadow(0 0 18px rgba(215, 235, 255, 0.12));
}

.ai-chat-label {
  min-width: 0;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.15;
  color: rgba(255, 255, 255, 0.96);
  white-space: nowrap;
}

@keyframes aiAuraSpin {
  from {
    transform: rotate(0deg) scale(0.9);
  }

  to {
    transform: rotate(360deg) scale(0.9);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-chat-orb::before {
    animation: none;
  }
}

@media (max-width: 680px) {
  .ai-chat-menu-button {
    min-width: 60px;
  }

  .ai-chat-label {
    display: none;
  }
}
</style>
